import { FC } from 'react';
import { HighlightOffRounded } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  styled,
} from '@mui/material';
import {
  useIsLdtSalesOrCustomerSuccessUser,
  useUser,
} from 'src/hooks/use_auth';
import { LdtUploadedFileFieldsFragment, UploadedFileStatus } from 'src/types';

interface UploadedFileItemProps {
  file: LdtUploadedFileFieldsFragment;
  /** Called when user clicks remove. Remove button is only shown when the current user is allowed to remove. */
  onRemove?: (fileId: string) => void;
  index?: number;
  isRemoving?: boolean;
}

const FileItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  padding: theme.spacing(0.5),
}));

const FileNameTypography = styled(Typography)(() => ({
  flex: 1,
  minWidth: 0,
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
}));

const StatusTypography = styled(Typography)<{
  status: UploadedFileStatus;
}>(({ theme, status }) => {
  const getColor = () => {
    switch (status) {
      case UploadedFileStatus.Complete:
        return theme.palette.success.main;
      case UploadedFileStatus.Failed:
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  return {
    flexShrink: 0,
    fontSize: theme.typography.caption.fontSize,
    color: getColor(),
  };
});

const RemoveButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

export const UploadedFileItem: FC<UploadedFileItemProps> = ({
  file,
  onRemove,
  index,
  isRemoving = false,
}) => {
  const userInfo = useUser();
  const isCsOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();
  const canRemove =
    !file.isAutoGenerated &&
    // If uploaderAuth0Id is not set, the file is not uploaded by a user; therefore,
    // users should not be able to remove it.
    file.uploaderAuth0Id &&
    (file.uploaderAuth0Id === userInfo.id || isCsOrSalesUser) &&
    !!onRemove;
  const effectiveOnRemove = canRemove ? onRemove : undefined;

  const status = file.status;
  const isComplete = status === UploadedFileStatus.Complete;
  const isInProgress = status === UploadedFileStatus.InProgress;
  const isFailed = status === UploadedFileStatus.Failed;
  const showProgress = isInProgress;

  const getStatusText = () => {
    if (isRemoving) return 'Removing...';
    if (isComplete) return 'Completed';
    if (isInProgress) return 'Uploading...';
    if (isFailed) return 'Error';
    return '';
  };

  const getRemoveButtonTitle = () => {
    if (isInProgress) return 'Cancel upload';
    return 'Remove file';
  };

  return (
    <FileItemContainer key={file.id || index}>
      <FileNameTypography variant='body2'>{file.filename}</FileNameTypography>
      {status && (
        <StatusTypography variant='caption' status={status}>
          {getStatusText()}
        </StatusTypography>
      )}
      {showProgress && (
        <CircularProgress
          variant='determinate'
          value={file.progress ?? 0}
          size={16}
          thickness={2}
          sx={{ flexShrink: 0 }}
        />
      )}
      {effectiveOnRemove && !isRemoving && (
        <RemoveButton
          size='small'
          onClick={() => effectiveOnRemove(file.id)}
          title={getRemoveButtonTitle()}
        >
          <HighlightOffRounded fontSize='small' />
        </RemoveButton>
      )}
    </FileItemContainer>
  );
};
